import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from "lucide-react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import {
  useIgIceBreakers,
  useSaveIgIceBreakers,
  useDeleteIgIceBreakers,
  type IceBreakerPrompt,
} from "@/hooks/use-ig-icebreakers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import type { ApiClientError } from "@/lib/api-client";

const MAX_PROMPTS = 4;
const Q_MAX = 80;
const P_MAX = 1000;

export function BrandSettingsInboxPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { role } = usePermissions();
  const brandId = brand?.id ?? "";
  const isAdmin = role === "ADMIN";

  const query = useIgIceBreakers(brandId);
  const save = useSaveIgIceBreakers(brandId);
  const del = useDeleteIgIceBreakers(brandId);

  const [prompts, setPrompts] = useState<IceBreakerPrompt[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (query.data) {
      setPrompts(query.data.prompts);
      setDirty(false);
    }
  }, [query.data]);

  if (!brand) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Hidden (INBOX_DISABLED) — null data after 404.
  if (query.data === null) {
    return (
      <div className="px-6 py-8 w-full max-w-2xl">
        <header className="mb-4">
          <h1 className="text-2xl font-semibold">{t("inbox.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("inbox.subtitle")}
          </p>
        </header>
        <div className="rounded-md border border-border bg-surface-1 p-6">
          <h2 className="text-base font-medium">{t("inbox.connectFirst")}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t("inbox.connectFirstNote")}
          </p>
          <Button asChild className="mt-4" size="sm">
            <Link to={`/${brand.slug}/settings/accounts`}>{t("inbox.connectInstagram")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  function updatePrompt(idx: number, patch: Partial<IceBreakerPrompt>) {
    setPrompts((curr) => curr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
    setDirty(true);
  }

  function addPrompt() {
    if (prompts.length >= MAX_PROMPTS) return;
    setPrompts((curr) => [...curr, { question: "" }]);
    setDirty(true);
  }

  function removePrompt(idx: number) {
    setPrompts((curr) => curr.filter((_, i) => i !== idx));
    setDirty(true);
  }

  function move(idx: number, dir: -1 | 1) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= prompts.length) return;
    setPrompts((curr) => {
      const next = [...curr];
      const tmp = next[idx];
      next[idx] = next[newIdx];
      next[newIdx] = tmp;
      return next;
    });
    setDirty(true);
  }

  function validateLocal(): string | null {
    if (prompts.length > MAX_PROMPTS) return t("inbox.validationMaxPrompts", { max: MAX_PROMPTS });
    for (let i = 0; i < prompts.length; i++) {
      const p = prompts[i];
      const q = p.question.trim();
      if (q.length < 1 || q.length > Q_MAX) return t("inbox.validationQuestion", { num: i + 1, max: Q_MAX });
      if (p.payload !== undefined && p.payload !== "" && p.payload.length > P_MAX) {
        return t("inbox.validationPayload", { num: i + 1, max: P_MAX });
      }
    }
    return null;
  }

  async function handleSave() {
    const err = validateLocal();
    if (err) {
      toast.error(err);
      return;
    }
    const sanitized = prompts.map((p) => {
      const out: IceBreakerPrompt = { question: p.question.trim() };
      if (p.payload && p.payload.length > 0) out.payload = p.payload;
      return out;
    });
    try {
      await save.mutateAsync(sanitized);
      toast.success(t("inbox.saved"));
      setDirty(false);
    } catch (e) {
      const apiErr = e as ApiClientError;
      toast.error(apiErr?.message ?? t("inbox.failedSave"));
    }
  }

  async function handleDelete() {
    try {
      await del.mutateAsync();
      toast.success(t("inbox.removed"));
      setPrompts([]);
      setDirty(false);
    } catch (e) {
      const apiErr = e as ApiClientError;
      toast.error(apiErr?.message ?? t("inbox.failedRemove"));
    }
  }

  return (
    <div className="px-6 py-8 w-full max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("inbox.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("inbox.subtitle")}
        </p>
      </header>

      <section className="rounded-md border border-border bg-surface-1 p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium">{t("inbox.iceBreakers")}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t("inbox.iceBreakersNote", { max: MAX_PROMPTS })}
          </p>
        </div>

        {!isAdmin && (
          <p className="text-xs text-muted-foreground italic">
            {t("inbox.adminOnly")}
          </p>
        )}

        {prompts.length === 0 && (
          <p className="text-sm text-muted-foreground">{t("inbox.noIceBreakers")}</p>
        )}

        <div className="space-y-3">
          {prompts.map((p, idx) =>
            isAdmin ? (
              <div
                key={idx}
                className="rounded-md border border-border bg-surface-0 p-3 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground">{t("inbox.questionLabel")}</label>
                    <Input
                      value={p.question}
                      maxLength={Q_MAX}
                      onChange={(e) => updatePrompt(idx, { question: e.target.value })}
                      placeholder={t("inbox.questionPlaceholder")}
                    />
                    <div className="text-[10px] text-muted-foreground mt-0.5 text-right">
                      {p.question.length} / {Q_MAX}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      aria-label={t("inbox.moveUp")}
                      className="p-1 rounded hover:bg-surface-2 text-muted-foreground disabled:opacity-30"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === prompts.length - 1}
                      aria-label={t("inbox.moveDown")}
                      className="p-1 rounded hover:bg-surface-2 text-muted-foreground disabled:opacity-30"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePrompt(idx)}
                    aria-label={t("inbox.removePrompt")}
                    className="p-1 rounded hover:bg-surface-2 text-muted-foreground"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">
                    {t("inbox.payloadLabel")}
                  </label>
                  <Input
                    value={p.payload ?? ""}
                    maxLength={P_MAX}
                    onChange={(e) =>
                      updatePrompt(idx, { payload: e.target.value || undefined })
                    }
                    placeholder={t("inbox.payloadPlaceholder")}
                  />
                  <div className="text-[10px] text-muted-foreground mt-0.5 text-right">
                    {(p.payload ?? "").length} / {P_MAX}
                  </div>
                </div>
              </div>
            ) : (
              <div
                key={idx}
                className="rounded-md border border-border bg-surface-0 p-3"
              >
                <p className="text-sm font-medium">{p.question}</p>
                {p.payload && (
                  <p className="text-xs text-muted-foreground mt-1">{t("inbox.payloadDisplay", { value: p.payload })}</p>
                )}
              </div>
            ),
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addPrompt}
              disabled={prompts.length >= MAX_PROMPTS}
            >
              <Plus size={14} className="mr-1" />
              {t("inbox.addPrompt")}
            </Button>

            <div className="flex items-center gap-2">
              <AlertDialog.Root>
                <AlertDialog.Trigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={del.isPending || query.data?.prompts.length === 0}
                  >
                    {t("inbox.resetAll")}
                  </Button>
                </AlertDialog.Trigger>
                <AlertDialog.Portal>
                  <AlertDialog.Overlay className="fixed inset-0 bg-black/30 z-50" />
                  <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface-0 border border-border rounded-md p-6 w-[420px] max-w-[90vw] shadow-lg">
                    <AlertDialog.Title className="text-base font-semibold">
                      {t("inbox.removeAllTitle")}
                    </AlertDialog.Title>
                    <AlertDialog.Description className="text-sm text-muted-foreground mt-2">
                      {t("inbox.removeAllDescription")}
                    </AlertDialog.Description>
                    <div className="flex justify-end gap-2 mt-4">
                      <AlertDialog.Cancel asChild>
                        <Button type="button" variant="outline" size="sm">
                          {t("actions.cancel", { ns: "common" })}
                        </Button>
                      </AlertDialog.Cancel>
                      <AlertDialog.Action asChild>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleDelete}
                        >
                          {t("inbox.removeAll")}
                        </Button>
                      </AlertDialog.Action>
                    </div>
                  </AlertDialog.Content>
                </AlertDialog.Portal>
              </AlertDialog.Root>

              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={!dirty || save.isPending}
              >
                {save.isPending && <Loader2 size={14} className="mr-1 animate-spin" />}
                {t("inbox.saveChanges")}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
